<template>
  <div>
    <!--    TransUnion Results    -->
    <template v-if="hasCreditPlusCreditResults">
      <DropDownCardV2 class="mb-3" labelClasses="d-sm-none">
        <template v-slot:header>
          <div class="p-4">
            <div class="row">
              <div class="col-12 col-sm-7">
                <h5 class="mb-3">{{ $t('message.credit.creditPlus.creditVisionScore') }}</h5>
                <p class="mt-0 d-none d-sm-block small">{{ $t('message.credit.creditPlus.msg') }}</p>
                <p class="d-none d-sm-block small">{{ $t('message.credit.creditPlus.msg2') }}</p>
              </div>
              <div class="col-12 col-sm-5">
                <div class="credit-plus-range-container mt-sm-4 mb-sm-3">
                  <img
                    :alt="creditVisionScoreImageName"
                    class="mx-auto d-block"
                    :src="require(`@/assets/credit-range/${creditVisionScoreImageName}.svg`)"
                    data-test="creditVisionScoreImage"
                  />
                  <div>
                    <h2 data-test="creditVisionScore">{{ creditVisionScore }}</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-slot:label>
          <div class="p-4 border-top">
            <h5 class="text-muted m-0">{{ $t('message.credit.creditPlus.understandYourScore') }}</h5>
          </div>
        </template>
        <template v-slot:dropdown>
          <div class="px-4 pb-4 d-sm-none">
            <p class="mt-0 small">{{ $t('message.credit.creditPlus.msg') }}</p>
            <p class="small">{{ $t('message.credit.creditPlus.msg2') }}</p>
          </div>
        </template>
      </DropDownCardV2>
      <loan-list :trade-lines="tradeLines"></loan-list>
    </template>
    <!--    Regional and Equifax Results   -->
    <template v-else>
      <div class="row">
        <div class="col-sm-6 mb-4">
          <DropDownCardV2 labelClasses="d-sm-none" class="h-100">
            <template v-slot:header>
              <div class="credit-plus-range-container text-center p-4">
                <h5 class="mb-3">
                  {{
                    displayEquifaxBranding
                      ? $t('message.credit.equifax.heading')
                      : $t('message.credit.regional.heading')
                  }}
                </h5>
                <img
                  alt=""
                  class="mx-auto d-block py-3"
                  :src="require(`@/assets/credit-range/${imageName}.svg`)"
                  data-test="creditRangeImage"
                />
                <div class="mt-4 pt-3">
                  <h4 data-test="creditRangeRange">{{ creditRangeRange }}</h4>
                  <img
                    v-if="displayEquifaxBranding"
                    alt="Equifax"
                    width="80"
                    :src="require('@/assets/credit-bureaus/Equifax-Logo.svg')"
                  />
                </div>
              </div>
            </template>
            <template v-slot:label>
              <div class="border-top understand-score-header">
                <h5 class="text-muted m-0">{{ $t('message.credit.equifax.understandTheScore') }}</h5>
              </div>
            </template>
            <template v-slot:dropdown>
              <div class="px-4 d-sm-none">
                <p class="mt-0 mb-1 small">
                  {{ $t('message.credit.equifax.understandTheScoreMsg') }}
                </p>
              </div>
              <div class="p-4 d-sm-none">
                <mobialis-highlighted-list
                  class="small text-dark mb-0"
                  :items="creditRanges"
                  :active-item="creditRange"
                >
                </mobialis-highlighted-list>
              </div>
            </template>
          </DropDownCardV2>
        </div>
        <div class="col-sm-6 mb-4 d-none d-sm-block">
          <div class="avr-card p-4 border-0 h-100">
            <mobialis-highlighted-list class="small text-dark mb-0" :items="creditRanges" :active-item="creditRange">
            </mobialis-highlighted-list>
          </div>
        </div>
      </div>
      <DropDownCardV2 class="mb-4 d-none d-sm-block">
        <template v-slot:label>
          <div class="p-4">
            <h5 class="m-0">{{ $t('message.credit.equifax.understandTheScore') }}</h5>
          </div>
        </template>
        <template v-slot:dropdown>
          <div class="px-4 pb-4">
            <p class="mt-0 small">{{ $t('message.credit.equifax.understandTheScoreMsg') }}</p>
          </div>
        </template>
      </DropDownCardV2>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DropDownCardV2 from '@/components/DropDownCardV2.vue';
import LoanList from '@/components/credit/LoanList.vue';
import helperFunctions from '@/utils/helperFunctions';
import { creditRangesMixin } from '@/mixins';

export default {
  name: 'CreditResultDetails',
  components: {
    DropDownCardV2,
    LoanList,
  },
  mixins: [creditRangesMixin],
  props: {
    creditVisionScore: {
      type: Number,
      required: false,
      validator: prop => prop >= 350 && prop <= 900,
    },
    creditRange: {
      type: String,
      required: false,
      validator: prop => ['A', 'B', 'C', 'D', 'E'].includes(prop),
    },
    tradeLines: {
      type: Array,
      required: false,
    },
  },
  computed: {
    ...mapGetters({
      hasCreditPlusCreditResults: 'credit/hasCreditPlusCreditResults',
      displayEquifaxBranding: 'business/displayEquifaxBranding',
    }),
    creditVisionScoreImageName() {
      return helperFunctions.creditVisionScoreImageName(this.creditVisionScore);
    },
    imageName() {
      return this.hasCreditPlusCreditResults ? this.creditVisionScoreImageName : `${this.creditRange}-range`;
    },
    creditRangeRange() {
      return this.getCreditRangeRange(this.creditRange);
    },
  },
};
</script>

<style lang="scss" scoped>
.credit-plus-range-container {
  position: relative;
  img {
    max-width: 100%;
  }
  div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }
  h2 {
    font-size: 35px;
    margin: 10px auto 0;
  }
}
@media (max-width: $breakpoint-mobile) {
  .understand-score-header {
    padding: 1rem 1.5rem 1rem 1.5rem !important;
  }
}
</style>
